import { format, isValid } from "date-fns";
import { enNZ } from "date-fns/locale";
import { User } from "oidc-client";
import {
  AdviceStatuses,
  ApplicationAssetAllocation,
  ApplicationClientDto,
  ApplicationClientRoles,
  ApplicationDto,
  ClientDto,
  ClientStatuses,
  ConfirmationType,
  DistributionOptions,
  IncomeGeneratorApplicationDto,
  KiwiSaverApplicationDto,
  Product,
  SearchClientDto,
  WealthBuilderApplicationDto,
} from "../../api/apiClients";
import { getApplications } from "../../common/helpers";

export interface ExtendedApplicationAssetAllocation extends ApplicationAssetAllocation {
  nickname?: string;
  isJoint?: boolean;
  product?: Product;
  distributionOption?: DistributionOptions | undefined;
  active?: boolean;
}

const getApplicationClients = (client: ClientDto): ApplicationClientDto[] => {
  const applications = getApplications(client);
  const applicationClients = applications.map((a) => a.applicationClients || []);
  if (applicationClients.length === 0) return [];
  return applicationClients.reduce((a, b) => a.concat(b));
};

export const getJointClientApplications = (client: SearchClientDto | undefined): ApplicationClientDto[] => {
  const jointClients = getApplicationClients({ ...client }).filter((ac) => ac.role === ApplicationClientRoles.Joint);
  return [...new Map(jointClients.map((ac) => [ac.clientId, ac])).values()];
};

export const getAssignedProducts = (client: SearchClientDto): Product[] => [
  ...(client.kiwiSaverApplication ? [Product.KiwiSaver] : []),
  ...(client.wealthBuilderApplications?.some((a) => a) ? [Product.WealthBuilder] : []),
  ...(client.incomeGeneratorApplications?.some((a) => a) ? [Product.IncomeGenerator] : []),
];

export const getAppliedProducts = (client: SearchClientDto): Product[] => [
  ...(client.kiwiSaverApplication?.active ? [Product.KiwiSaver] : []),
  ...(client.wealthBuilderApplications?.some((a) => a.active) ? [Product.WealthBuilder] : []),
  ...(client.incomeGeneratorApplications?.some((a) => a.active) ? [Product.IncomeGenerator] : []),
];

export const getAllApplicants = (client: ClientDto | undefined): ApplicationClientDto[] => {
  const applications = getApplications({ ...client });
  const apps = applications.map((a) => a.applicationClients || []);
  if (apps.length === 0) return [];
  return [...new Map(apps.reduce((a, b) => a.concat(b)).map((ac) => [ac.clientId, ac])).values()];
};

export const getApplicationsByProduct = (client: ClientDto | undefined, product: Product): ApplicationDto[] => {
  const applications = getApplications({ ...client });
  const apps = applications.filter((p) => p.product === product).map((a) => a || []);
  if (apps.length === 0) return [];
  return [...new Map(apps.map((ac) => [ac.applicationId, ac])).values()];
};

export const sortApplicationClients = (applicationClients: ApplicationClientDto[]): ApplicationClientDto[] => {
  if (applicationClients.length === 0) return [];
  return applicationClients.slice().sort((app1, app2) => {
    if (Object.keys(ApplicationClientRoles).indexOf(app1.role!) > Object.keys(ApplicationClientRoles).indexOf(app2.role!)) return 1;
    if (Object.keys(ApplicationClientRoles).indexOf(app1.role!) < Object.keys(ApplicationClientRoles).indexOf(app2.role!)) return -1;
    return 0;
  });
};

const getAllApplications = (client: ClientDto): (ApplicationDto & { product: Product })[] => [
  ...(client?.kiwiSaverApplication ? [{ product: Product.KiwiSaver, ...client?.kiwiSaverApplication }] : []),
  ...(client?.wealthBuilderApplications?.some((a) => a) ? client?.wealthBuilderApplications.map((app) => ({ product: Product.WealthBuilder, ...app })) : []),
  ...(client?.incomeGeneratorApplications?.some((a) => a) ? client?.incomeGeneratorApplications.map((app) => ({ product: Product.IncomeGenerator, ...app })) : []),
];

export const applicationNeedsConfirmation = (client: ClientDto): boolean =>
  client?.adviceStatus === AdviceStatuses.Completed && client?.clientStatus === ClientStatuses.Completed && !client?.confirmationType?.split(", ").includes(ConfirmationType.Confirmed);

export const getAllProductApplications = (client: ClientDto, primaryOnly?: boolean): ExtendedApplicationAssetAllocation[] => {
  const applications = getAllApplications(client);
  const applicationAssetAllocations = [] as ExtendedApplicationAssetAllocation[];
  const filtered = primaryOnly ? applications.filter((app) => !app.applicationClients?.some((c) => c.role === ApplicationClientRoles.Joint)) : applications;

  filtered.map((app) => {
    if (app.product == Product.WealthBuilder) {
      const application = app as WealthBuilderApplicationDto;
      applicationAssetAllocations.push({
        applicationId: application.applicationId,
        nickname: application.nickname,
        income: application.income,
        inflation: application.inflation,
        growth: application.growth,
        investmentOption: application.investmentOption,
        isJoint: application.applicationClients?.some((c) => c.role === ApplicationClientRoles.Joint),
        product: Product.WealthBuilder,
        active: app.active,
      });
    } else if (app.product == Product.KiwiSaver) {
      const application = app as KiwiSaverApplicationDto;
      applicationAssetAllocations.push({
        applicationId: application.applicationId,
        nickname: application.nickname,
        income: application.income,
        inflation: application.inflation,
        growth: application.growth,
        investmentOption: application.investmentOption,
        isJoint: application.applicationClients?.some((c) => c.role === ApplicationClientRoles.Joint),
        product: Product.KiwiSaver,
        active: app.active,
      });
    } else if (app.product == Product.IncomeGenerator) {
      const application = app as IncomeGeneratorApplicationDto;
      applicationAssetAllocations.push({
        applicationId: application.applicationId,
        nickname: application.nickname,
        isJoint: application.applicationClients?.some((c) => c.role === ApplicationClientRoles.Joint),
        product: Product.IncomeGenerator,
        distributionOption: application.distributionOption,
        active: app.active,
        income: undefined,
        inflation: undefined,
        growth: undefined,
      });
    }
  });

  return applicationAssetAllocations;
};

export const formatDate = (date?: Date) => (!!date && isValid(new Date(date)) ? format(new Date(date), "dd MMM yyyy", { locale: enNZ }) : "");

export const getRoles = (user: User | undefined): string[] => (typeof user?.profile.role === "string" ? [user.profile.role] : (user?.profile?.role as string[]) || []);

export const hasSelectedProductRoles = (user: User | undefined, products: Product[] | undefined) => {
  const roles = getRoles(user);
  return !!products?.length && products.every((product) => roles.includes(product));
};
